import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const TIER_RADIUS = { 1: 1.0, 2: 1.9, 3: 2.8, 4: 3.7, 5: 4.6 };
const HOME = [6.0, 3.1, 8.6];

/** Soft white falloff, drawn once and shared by every glow in the scene. */
const makeGlow = () => {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grd.addColorStop(0, "rgba(255,255,255,1)");
  grd.addColorStop(0.16, "rgba(255,255,255,0.6)");
  grd.addColorStop(0.42, "rgba(255,255,255,0.14)");
  grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd;
  g.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

const ringGeometry = (radius, segments = 128) => {
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    pts.push(new THREE.Vector3(Math.cos(a) * radius, 0, Math.sin(a) * radius));
  }
  return new THREE.BufferGeometry().setFromPoints(pts);
};

/** A shell read as a globe: meridians and parallels, not a wireframe ball. */
const buildShell = (radius, opacity, detailed) => {
  const group = new THREE.Group();
  const mat = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity,
    depthWrite: false,
  });
  const meridians = detailed ? 16 : 3;
  for (let i = 0; i < meridians; i++) {
    const ring = new THREE.Line(ringGeometry(radius), mat);
    ring.rotation.z = Math.PI / 2;
    ring.rotation.y = (i / meridians) * Math.PI;
    group.add(ring);
  }
  if (detailed) {
    for (let i = 1; i < 8; i++) {
      const lat = (i / 8) * Math.PI - Math.PI / 2;
      const r = Math.cos(lat) * radius;
      if (r < 0.02) continue;
      const ring = new THREE.Line(ringGeometry(r), mat);
      ring.position.y = Math.sin(lat) * radius;
      group.add(ring);
    }
  } else {
    const equator = new THREE.Line(ringGeometry(radius), mat);
    group.add(equator);
  }
  return group;
};

const dustField = () => {
  const n = 1400;
  const pos = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const r = 26 + Math.random() * 64;
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    pos[i * 3] = r * Math.sin(ph) * Math.cos(th);
    pos[i * 3 + 1] = r * Math.cos(ph) * 0.5;
    pos[i * 3 + 2] = r * Math.sin(ph) * Math.sin(th);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  return new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.16,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
    })
  );
};

/**
 * Nested concentric tier shells, deterministic category nodes and influence
 * edges that dive through the core. Drag to orbit, scroll to zoom. Monochrome only.
 */
export const MaslowSphere = ({
  map,
  visibleTiers = [1, 2, 3, 4, 5],
  showCandidates = false,
  onSelect,
  resetSignal = 0,
  heat = null,
  activeIds = null,
  compare = null,
  autoRotate = true,
  dust: showDust = true,
}) => {
  const host = useRef(null);
  const labelHost = useRef(null);
  const api = useRef({});
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const el = host.current;
    if (!el) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 400);
    camera.position.set(...HOME);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.cursor = "grab";
    renderer.domElement.style.touchAction = "none";
    el.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.rotateSpeed = 0.6;
    controls.zoomSpeed = 0.75;
    controls.enablePan = false;
    controls.minDistance = 2.0;
    controls.maxDistance = 26;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3;

    // the drift is only there to say "this moves" — the moment you take hold, it stops
    let idleAt = 0;
    const seize = () => {
      controls.autoRotate = false;
      renderer.domElement.style.cursor = "grabbing";
      api.current.onTouched?.();
    };
    const release = () => {
      idleAt = performance.now();
      renderer.domElement.style.cursor = "grab";
    };
    controls.addEventListener("start", seize);
    controls.addEventListener("end", release);

    scene.add(new THREE.AmbientLight(0xffffff, 1.0));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(4, 6, 6);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xffffff, 0.5);
    rim.position.set(-6, -3, -5);
    scene.add(rim);

    const glowTex = makeGlow();
    const dust = dustField();
    dust.visible = api.current.showDust !== false;
    scene.add(dust);

    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.15, 2),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    const coreGlow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTex,
        transparent: true,
        opacity: 0.42,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    coreGlow.scale.setScalar(1.05);
    scene.add(core, coreGlow);

    const shellGroup = new THREE.Group();
    const nodeGroup = new THREE.Group();
    const glowGroup = new THREE.Group();
    const edgeGroup = new THREE.Group();
    const pulseGroup = new THREE.Group();
    scene.add(shellGroup, edgeGroup, nodeGroup, glowGroup, pulseGroup);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2(-10, -10);
    let hovered = null;

    const onMove = (e) => {
      const r = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      pointer.y = -((e.clientY - r.top) / r.height) * 2 + 1;
    };
    const onLeave = () => pointer.set(-10, -10);
    const onClick = () => {
      const n = hovered?.userData?.node;
      if (n && !n.anonymous) api.current.onSelect?.(n);
    };
    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("pointerleave", onLeave);
    renderer.domElement.addEventListener("click", onClick);

    const resize = () => {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    const t0 = performance.now();
    let raf;
    const tmp = new THREE.Vector3();

    const tick = () => {
      const t = (performance.now() - t0) / 1000;
      if (!controls.autoRotate && idleAt && performance.now() - idleAt > 7000) {
        controls.autoRotate = true;
        idleAt = 0;
      }
      controls.update();

      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(nodeGroup.children, false);
      const next = hits.length ? hits[0].object : null;
      if (next !== hovered) {
        hovered = next;
        const interactive = hovered && !hovered.userData.node?.anonymous;
        if (!controls.autoRotate || true) {
          renderer.domElement.style.cursor = interactive ? "pointer" : "grab";
        }
      }

      shellGroup.children.forEach((s, i) => {
        s.rotation.y = t * 0.016 * (i % 2 === 0 ? 1 : -1);
      });
      dust.rotation.y = t * 0.006;

      const active = api.current.activeIds;
      nodeGroup.children.forEach((m) => {
        const d = m.userData;
        d.shown = d.shown + (d.baseOpacity - d.shown) * 0.012;
        const lit = !!(active && d.node?.id && active.includes(d.node.id));
        const hot = m === hovered;
        // a gentle pulse in and out: slow, and shells breathe together so it reads
        // as a wave over the sphere rather than per-node flicker
        const speed = 0.5 + (d.heat || 0) * 0.9;
        const amp = (0.13 + (d.heat || 0) * 0.12) * Math.min(1, d.baseOpacity * 1.4);
        const wave = 0.5 + 0.5 * Math.sin(t * speed + d.pulse);
        const breath = 1 - amp + amp * wave;
        const o = Math.min(1, d.shown * breath * (lit ? 2.4 : 1));
        m.material.opacity = o;
        const target =
          (d.targetScale || 1) * (lit ? 2.2 : 1) * (hot ? 1.7 : 1) * (1 + (wave - 0.5) * 0.09);
        m.scale.setScalar(m.scale.x + (target - m.scale.x) * 0.12);
        if (d.glow) {
          d.glow.material.opacity = Math.min(
            0.95,
            o * (d.glowStrength || 0.5) * (lit ? 2.6 : 1) * (hot ? 1.6 : 1)
          );
          const gs = d.glowScale * (lit ? 1.5 : 1) * (hot ? 1.35 : 1);
          d.glow.scale.setScalar(d.glow.scale.x + (gs - d.glow.scale.x) * 0.12);
        }
      });

      pulseGroup.children.forEach((s) => {
        const d = s.userData;
        const p = (t * d.speed + d.offset) % 1;
        s.position.copy(d.curve.getPoint(p));
        s.material.opacity = Math.sin(Math.PI * p) * d.peak;
      });

      core.rotation.y = t * 0.22;
      core.rotation.x = t * 0.1;
      coreGlow.scale.setScalar(1.02 + Math.sin(t * 1.1) * 0.1);

      const lh = labelHost.current;
      if (lh) {
        const w = el.clientWidth;
        const h = el.clientHeight;
        for (const child of lh.children) {
          const nid = child.dataset.nodeId;
          const mesh = nodeGroup.children.find((m) => m.userData.node?.id === nid);
          if (!mesh) {
            child.style.opacity = "0";
            continue;
          }
          tmp.copy(mesh.position).project(camera);
          // fade a label out as its node swings round the back instead of blinking it off
          const depth = tmp.z;
          let want = depth > 0.995 ? 0 : mesh === hovered ? 1 : 0.5;
          if (want && depth > 0.985) want *= (0.995 - depth) / 0.01;
          child.style.transform = `translate(-50%,-50%) translate(${((tmp.x + 1) / 2) * w}px, ${((-tmp.y + 1) / 2) * h - 16}px)`;
          const now = parseFloat(child.style.opacity || "0");
          child.style.opacity = String(now + (want - now) * 0.12);
        }
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    api.current = {
      ...api.current,
      scene,
      camera,
      controls,
      shellGroup,
      nodeGroup,
      glowGroup,
      edgeGroup,
      pulseGroup,
      glowTex,
      renderer,
      dust,
      reset: () => {
        camera.position.set(...HOME);
        controls.target.set(0, 0, 0);
        controls.autoRotate = true;
        controls.update();
      },
    };

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.removeEventListener("start", seize);
      controls.removeEventListener("end", release);
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("pointerleave", onLeave);
      renderer.domElement.removeEventListener("click", onClick);
      controls.dispose();
      renderer.dispose();
      glowTex.dispose();
      el.removeChild(renderer.domElement);
    };
  }, []);

  useEffect(() => {
    api.current.showDust = showDust;
    if (api.current.dust) api.current.dust.visible = showDust;
  }, [showDust]);

  useEffect(() => {
    api.current.onSelect = onSelect;
  }, [onSelect]);

  useEffect(() => {
    api.current.onTouched = () => setTouched(true);
  }, []);

  // replay frames change constantly — route them through the ref, not a rebuild
  useEffect(() => {
    api.current.activeIds = activeIds;
  }, [activeIds]);

  useEffect(() => {
    if (api.current.controls) api.current.controls.autoRotate = autoRotate && !touched;
  }, [autoRotate, touched]);

  useEffect(() => {
    if (resetSignal) api.current.reset?.();
  }, [resetSignal]);

  // rebuild geometry whenever the map or tier filter changes
  useEffect(() => {
    const { shellGroup, nodeGroup, glowGroup, edgeGroup, pulseGroup, glowTex } = api.current;
    if (!shellGroup || !map) return;

    const wipe = (g) =>
      [...g.children].forEach((c) => {
        g.remove(c);
        c.traverse?.((x) => {
          x.geometry?.dispose?.();
          if (x.material && x.material !== c.material) x.material.dispose?.();
        });
        c.geometry?.dispose?.();
        c.material?.dispose?.();
      });
    [shellGroup, nodeGroup, glowGroup, edgeGroup, pulseGroup].forEach(wipe);

    const outer = Math.max(...visibleTiers);
    (map.tiers || []).forEach((t) => {
      if (!visibleTiers.includes(t.tier)) return;
      const radius = TIER_RADIUS[t.tier] ?? t.radius;
      shellGroup.add(
        buildShell(radius, t.tier === outer ? 0.058 : 0.036, t.tier === outer)
      );
    });

    (map.nodes || []).forEach((n) => {
      if (!visibleTiers.includes(n.tier)) return;
      const anon = !!n.anonymous;
      const presence = anon ? (n.presence ?? 0) : n.salience / 100;
      const filled = presence > 0;
      const h = heat ? (heat[n.id] ?? 0) : 0;
      const side = compare ? n.side || compare[n.id] : null;
      let target = n.opacity ?? (filled ? 0.92 : 0.16);
      if (side) target = side === "shared" ? 0.95 : side === "none" ? 0.08 : 0.6;

      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(0.035, n.radius * 0.72), 20, 20),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.02,
          depthWrite: false,
        })
      );
      mesh.position.fromArray(n.position);
      mesh.renderOrder = 2;

      const glow = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: glowTex,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      glow.position.copy(mesh.position);
      const glowScale = Math.max(0.3, n.radius * 5.2) * (anon ? 0.7 : 1);
      glow.scale.setScalar(glowScale);
      glowGroup.add(glow);

      mesh.scale.setScalar(anon ? 0.6 : 1);
      mesh.userData = {
        node: n,
        baseOpacity: target,
        heat: h,
        glow,
        glowScale,
        glowStrength: side === "b" ? 0.25 : anon ? 0.3 + 0.5 * presence : 0.35 + 0.5 * presence,
        // the operator views are instruments, not reveals: show them immediately
        shown: heat || compare ? target : 0.02,
        pulse: n.tier * 1.3,
        targetScale: anon ? 0.6 + 0.4 * presence : 1,
      };
      nodeGroup.add(mesh);
    });

    // emergent facets: small satellites orbiting their parent, tethered so the lineage reads
    const parentById = {};
    (map.nodes || []).forEach((n) => {
      parentById[n.id] = n;
    });
    (map.facets || []).forEach((f) => {
      if (!visibleTiers.includes(f.tier)) return;
      const parent = parentById[f.parent];
      if (!parent) return;
      const presence = (f.salience || 0) / 100;

      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(0.022, f.radius * (f.promoted ? 1 : 0.7)), 14, 14),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.02,
          depthWrite: false,
        })
      );
      mesh.position.fromArray(f.position);
      mesh.renderOrder = 2;
      mesh.userData = {
        node: {
          id: f.id,
          name: f.name,
          tier: f.tier,
          salience: f.salience,
          valence: f.valence,
          evidence: f.evidence,
          business_lens: f.gloss || `A sub-need the Cartographer named under ${f.parent_name}.`,
          tier_name: parent.tier_name,
          facet: true,
          promoted: f.promoted,
          parent_name: f.parent_name,
        },
        baseOpacity: (f.promoted ? 0.45 : 0.3) + 0.55 * presence,
        heat: 0,
        glowStrength: 0,
        shown: heat || compare ? 0.3 + 0.55 * presence : 0.02,
        pulse: f.tier * 1.3 + 0.45,
        targetScale: 1,
      };
      nodeGroup.add(mesh);

      const tether = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3().fromArray(parent.position),
          new THREE.Vector3().fromArray(f.position),
        ]),
        new THREE.LineBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: (f.promoted ? 0.22 : 0.1) + 0.18 * presence,
          depthWrite: false,
        })
      );
      edgeGroup.add(tether);
    });

    const drawEdge = (e, promoted) => {
      if (!e.from_position || !e.to_position) return;
      const a = new THREE.Vector3().fromArray(e.from_position);
      const b = new THREE.Vector3().fromArray(e.to_position);
      const mid = a.clone().add(b).multiplyScalar(0.5).multiplyScalar(0.42);
      const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
      const geo = new THREE.BufferGeometry().setFromPoints(curve.getPoints(64));
      const strength = e.strength ?? Math.max(e.a_strength || 0, e.b_strength || 0);
      const corrosive = e.valence === "corrosive";
      const dashed = e.side ? e.side === "b" : corrosive || !promoted;
      let line;
      if (dashed) {
        line = new THREE.Line(
          geo,
          new THREE.LineDashedMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: e.side || promoted ? 0.4 + (strength / 100) * 0.35 : 0.1,
            dashSize: e.side || promoted ? 0.12 : 0.05,
            gapSize: e.side || promoted ? 0.07 : 0.09,
            depthWrite: false,
          })
        );
        line.computeLineDistances();
      } else {
        line = new THREE.Line(
          geo,
          new THREE.LineBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.3 + (strength / 100) * 0.45,
            depthWrite: false,
          })
        );
      }
      edgeGroup.add(line);

      // a promoted line is live: something travels it, and you can see which way
      if (promoted && !e.side) {
        const sprite = new THREE.Sprite(
          new THREE.SpriteMaterial({
            map: glowTex,
            transparent: true,
            opacity: 0,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          })
        );
        sprite.scale.setScalar(corrosive ? 0.3 : 0.42);
        sprite.userData = {
          curve,
          speed: 0.1 + (strength / 100) * 0.16,
          offset: Math.random(),
          peak: corrosive ? 0.32 : 0.6,
        };
        pulseGroup.add(sprite);
      }
    };

    (map.influence_nodes || []).forEach((e) => drawEdge(e, true));
    if (showCandidates) (map.candidate_edges || []).forEach((e) => drawEdge(e, false));
  }, [map, visibleTiers, showCandidates, heat, compare]);

  const labelNodes = (map?.nodes || []).filter(
    (n) => visibleTiers.includes(n.tier) && !n.anonymous && n.name && n.salience >= 45
  );
  const labelFacets = (map?.facets || []).filter(
    (f) => visibleTiers.includes(f.tier) && f.name && (f.promoted || f.salience >= 60)
  );

  return (
    <div className="relative h-full w-full" data-testid="maslow-sphere-canvas">
      <div ref={host} className="absolute inset-0" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 46%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.42) 78%, rgba(0,0,0,0.72) 100%)",
        }}
      />
      <div ref={labelHost} className="absolute inset-0 pointer-events-none overflow-hidden">
        {labelNodes.map((n) => (
          <span
            key={n.id}
            data-node-id={n.id}
            className="absolute font-data text-[9px] uppercase tracking-[0.18em] whitespace-nowrap text-zinc-200"
            style={{ left: 0, top: 0, opacity: 0 }}
          >
            {n.name}
          </span>
        ))}
        {labelFacets.map((f) => (
          <span
            key={f.id}
            data-node-id={f.id}
            data-testid={`sphere-facet-label-${f.id}`}
            className={`absolute whitespace-nowrap font-data text-[8px] tracking-[0.1em] ${
              f.promoted ? "text-zinc-200" : "italic text-zinc-400"
            }`}
            style={{ left: 0, top: 0, opacity: 0 }}
          >
            {f.name}
          </span>
        ))}
      </div>
      {!touched && (
        <p
          data-testid="sphere-interaction-hint"
          className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 font-data text-[10px] uppercase tracking-[0.22em] text-zinc-600"
        >
          drag to orbit · scroll to zoom
        </p>
      )}
    </div>
  );
};

export default MaslowSphere;
